/**
 * Tenant Authentication Context
 * ==============================
 * سياق مصادقة الشركات
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TenantAuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

export const TenantAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem('watrly_tenant_token');
  const getRefreshToken = () => localStorage.getItem('watrly_tenant_refresh_token');

  const setTokens = (accessToken, refreshToken) => {
    localStorage.setItem('watrly_tenant_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('watrly_tenant_refresh_token', refreshToken);
    }
  };

  const clearTokens = () => {
    localStorage.removeItem('watrly_tenant_token');
    localStorage.removeItem('watrly_tenant_refresh_token');
    localStorage.removeItem('watrly_tenant_user');
    localStorage.removeItem('watrly_tenant_info');
  };

  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearTokens();
        setUser(null);
        setTenant(null);
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'حدث خطأ');
    }

    return response.json();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/tenant/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'البريد الإلكتروني أو كلمة المرور غير صحيحة');
      }

      const data = await response.json();
      setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      setTenant(data.tenant);
      localStorage.setItem('watrly_tenant_user', JSON.stringify(data.user));
      localStorage.setItem('watrly_tenant_info', JSON.stringify(data.tenant));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setTenant(null);
  }, []);

  const refreshAuth = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/tenant/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      clearTokens();
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      const savedUser = localStorage.getItem('watrly_tenant_user');
      const savedTenant = localStorage.getItem('watrly_tenant_info');

      if (token && savedUser && savedTenant) {
        try {
          setUser(JSON.parse(savedUser));
          setTenant(JSON.parse(savedTenant));
          
          // Verify token is still valid
          const response = await fetch(`${API_BASE_URL}/tenant/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (!response.ok) {
            const refreshed = await refreshAuth();
            if (!refreshed) {
              logout();
            }
          }
        } catch {
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [logout, refreshAuth]);

  const value = {
    user,
    tenant,
    loading,
    error,
    isAuthenticated: !!user && !!tenant,
    login,
    logout,
    apiCall,
    getToken,
  };

  return (
    <TenantAuthContext.Provider value={value}>
      {children}
    </TenantAuthContext.Provider>
  );
};

export const useTenantAuth = () => {
  const context = useContext(TenantAuthContext);
  if (!context) {
    throw new Error('useTenantAuth must be used within TenantAuthProvider');
  }
  return context;
};

export default TenantAuthContext;
